import React from 'react';
import { Button, IconButton, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { InventoryOrder, InventoryOrderStatus } from '@api/InventoryOrders/InventoryOrder';
import { useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import { ProfileTypes } from '@api/Users/User';
import { BranchOffice } from '@api/BranchOffices/BranchOffice';
import CheckIcon from '@mui/icons-material/Check';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { currencyFormatter } from '@utils/Numbers';

interface InventoryOrderActionsProps {
    note: string;
    setNote: (note: string) => void;
    canModify: boolean;
    total: number;
    cashAmount?: number;
    creditCardAmount?: number;
    bankTransferAmount?: number;
    currentOrder?: InventoryOrder;
    onSaveOrder: () => void;
    onPayment: () => void;
    onPayWithCash?: () => void;
    onPayWithCard?: () => void;
    onPayWithTransfer?: () => void;
    onRemoveCash?: () => void;
    onRemoveCard?: () => void;
    onRemoveTransfer?: () => void;
    onMarkAsInRoute?: () => void;
    onMarkAsReceived?: () => void;
    showBranchSelector?: boolean;
    branchOffices?: BranchOffice[];
    selectedBranchOfficeId?: string;
    onBranchOfficeChange?: (id: string) => void;
}

export const InventoryOrderActions: React.FunctionComponent<InventoryOrderActionsProps> = ({
    note,
    setNote,
    canModify,
    total,
    cashAmount = 0,
    creditCardAmount = 0,
    bankTransferAmount = 0,
    currentOrder,
    onSaveOrder,
    onPayment,
    onPayWithCash,
    onPayWithCard,
    onPayWithTransfer,
    onRemoveCash,
    onRemoveCard,
    onRemoveTransfer,
    onMarkAsInRoute,
    onMarkAsReceived,
    showBranchSelector,
    branchOffices,
    selectedBranchOfficeId,
    onBranchOfficeChange
}) => {
    const { profileSelected } = useSelector((state: RootReducer) => state.auth);

    const canRegisterPayment =
        !!currentOrder &&
        currentOrder.paymentStatus !== 'paid' &&
        (profileSelected == ProfileTypes.Admin || profileSelected == ProfileTypes.DistributionCenter);

    const paidAmount = cashAmount + creditCardAmount + bankTransferAmount;
    const remainingAmount = Math.max(0, total - paidAmount);
    const isFullyPaid = total > 0 && Math.abs(total - paidAmount) < 0.005;

    return (
        <Stack spacing={2}>
            {showBranchSelector && branchOffices && onBranchOfficeChange && (
                <Select
                    fullWidth
                    size="small"
                    value={selectedBranchOfficeId || 'non'}
                    onChange={(e) => onBranchOfficeChange(e.target.value)}
                    displayEmpty
                >
                    <MenuItem value="non">Selecciona una sucursal</MenuItem>
                    {branchOffices.map((branchOffice) => (
                        <MenuItem key={branchOffice.id} value={branchOffice.id}>
                            {branchOffice.name}
                        </MenuItem>
                    ))}
                </Select>
            )}
            <TextField
                label="Nota del pedido (opcional)"
                multiline
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={!canModify}
            />

            {canRegisterPayment ? (
                <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body1" fontWeight={600}>
                            Subtotal
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                            {currencyFormatter(total)}
                        </Typography>
                    </Stack>
                    {cashAmount > 0 && (
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2">Pago en efectivo</Typography>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Typography variant="body2">{currencyFormatter(cashAmount)}</Typography>
                                {onRemoveCash && (
                                    <IconButton size="small" color="error" onClick={onRemoveCash}>
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Stack>
                        </Stack>
                    )}
                    {creditCardAmount > 0 && (
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2">Pago con tarjeta</Typography>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Typography variant="body2">{currencyFormatter(creditCardAmount)}</Typography>
                                {onRemoveCard && (
                                    <IconButton size="small" color="error" onClick={onRemoveCard}>
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Stack>
                        </Stack>
                    )}
                    {bankTransferAmount > 0 && (
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2">Pago con transferencia</Typography>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Typography variant="body2">{currencyFormatter(bankTransferAmount)}</Typography>
                                {onRemoveTransfer && (
                                    <IconButton size="small" color="error" onClick={onRemoveTransfer}>
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Stack>
                        </Stack>
                    )}
                    {remainingAmount > 0 && paidAmount > 0 && (
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="error">
                                Restante
                            </Typography>
                            <Typography variant="body2" color="error">
                                {currencyFormatter(remainingAmount)}
                            </Typography>
                        </Stack>
                    )}
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6" fontWeight={600}>
                            Total:
                        </Typography>
                        <Typography variant="h6" fontWeight={600} color="primary">
                            {currencyFormatter(total)}
                        </Typography>
                    </Stack>
                </Stack>
            ) : (
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight={600}>
                        Total:
                    </Typography>
                    <Typography variant="h6" fontWeight={600} color="primary">
                        {currencyFormatter(total)}
                    </Typography>
                </Stack>
            )}

            <Stack spacing={1}>
                {!currentOrder && (
                    <Button
                        variant="contained"
                        color="success"
                        fullWidth
                        startIcon={<CheckIcon />}
                        onClick={onSaveOrder}
                    >
                        Enviar Pedido
                    </Button>
                )}
                {canRegisterPayment && (
                    <Stack spacing={1}>
                        {remainingAmount > 0 && (
                            <Stack direction="row" spacing={1}>
                                {onPayWithCash && (
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        fullWidth
                                        startIcon={<AttachMoneyIcon />}
                                        onClick={onPayWithCash}
                                    >
                                        Efectivo
                                    </Button>
                                )}
                                {onPayWithCard && (
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        fullWidth
                                        startIcon={<CreditCardIcon />}
                                        onClick={onPayWithCard}
                                    >
                                        Tarjeta
                                    </Button>
                                )}
                                {onPayWithTransfer && (
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        fullWidth
                                        startIcon={<AccountBalanceIcon />}
                                        onClick={onPayWithTransfer}
                                    >
                                        Transferencia
                                    </Button>
                                )}
                            </Stack>
                        )}
                        <Button
                            variant="contained"
                            color="success"
                            fullWidth
                            disabled={!isFullyPaid}
                            onClick={onPayment}
                        >
                            Registrar Pago
                        </Button>
                    </Stack>
                )}
                {currentOrder?.status === InventoryOrderStatus.pickupReady && onMarkAsInRoute && (
                    <Button variant="contained" color="primary" fullWidth onClick={onMarkAsInRoute}>
                        Marcar como En Ruta
                    </Button>
                )}
                {currentOrder?.status === InventoryOrderStatus.inRoute && onMarkAsReceived && (
                    <Button variant="contained" color="success" fullWidth onClick={onMarkAsReceived}>
                        Marcar como Recibido
                    </Button>
                )}
            </Stack>
        </Stack>
    );
};

export default InventoryOrderActions;
